import { formatTime } from '../../vendor/helpers';
import ApplicationController from '../application_controller';
import { get, post } from '@rails/request.js';
import TC, { TurboConfirm } from '@rolemodel/turbo-confirm';

const FFT_SIZE = 1024;
const SMOOTHING_CONSTANT = 0.8;
const NUM_LEDS = 10;
const ledColor = [
  "#00B73D",
  "#00B73D",
  "#00B73D",
  "#00B73D",
  "#00B73D",
  "#00B73D",
  "#00B73D",
  "#f7931e",
  "#f7931e",
  "#f2555b",
]


export default class extends ApplicationController {
  static targets = [
    'audioSelect',
    'broadcastButton',
    'muteButton',
    'volume',
    'leftChannelLeds',
    'rightChannelLeds',
    'broadcastTimer',
    'closeEventButton',
    'cancelEventButton',
    'iframe',
    'audioSettingsButton',
    'audioSettings',
    'editEventButton',
    'timeRemaining',
    'loader',
    'statusBar',
    'liveStatus',
    'listenLiveButton',
    'networkBanner'
  ]

  static values = {
    eventId: String,
    permission: String,
    isMuted: Boolean,
    isRecording: Boolean,
    hasFinishedBroadcast: Boolean,
    liteNoBroadcast: Boolean,
    summaryUrl: String,
    broadcastTokenUrl: String,
    broadcastTimeUrl: String,
    broadcastStarted: Boolean,
    audioSettingsVisible: Boolean,
    appDownloadUrl: String,
    channelEventUrl: String,
    cancelEventUrl: String,
    chromePermissionsUrl: String,
    firefoxPermissionsUrl: String
  }

  async connect() {
    TC.start();
    this.castConnected = true;
    this.connectionOnline = true;
    await this.getStream().then(this.getDevices).then(this.gotDevices);
    this.checkNetworkConnection();

    if (this.liteNoBroadcastValue) {
      this.audioSettingsVisibleValue = true;
    }
  }

  disconnect() {
    if (this.connectionStatusInterval) {
      clearInterval(this.connectionStatusInterval);
    }
  }

  async getStream() {
    const audioSource = this.audioSelectTarget.value;
    const constraints = {
      audio: {
        noiseSuppression: false,
        echoCancellation: false,
        deviceId: audioSource ? {
          exact: audioSource
        } : undefined
      }
    };
    return navigator.mediaDevices.getUserMedia(constraints).
      then(this.gotStream).catch(this.handleError);
  }

  getDevices() {
    return navigator.mediaDevices.enumerateDevices();
  }

  gotDevices = (deviceInfos) => {
    console.log('[Browser Broadcast] - Available input and output devices:', deviceInfos);

    let inputDevices = deviceInfos.filter(d => 'audioinput' === d.kind).filter(d => d.deviceId && '' !== d.deviceId).map((d, t) => ({
      deviceId: d.deviceId,
      label: d.label || 'Microphone '.concat(t + 1)
    }));

    for (const input of inputDevices) {
      const option = document.createElement('option');
      option.value = input.deviceId;
      option.text = input.label;
      this.audioSelectTarget.appendChild(option);
    }
    this.audioSelectTarget.style.display = 'block';
    this.audioSelectTarget.removeAttribute('disabled');
  }

  gotStream = (stream) => {
    this.microphoneStream = stream;

    if (this.sourceNode) {
      console.log('[Browser Broadcast] - Swapping inputs');
      // Disconnect the current source from the gainNode
      this.sourceNode.disconnect(this.gainNode);
      this.sourceNode = new MediaStreamAudioSourceNode(this.audioContext, {
        mediaStream: stream
      });
      // Connect the new one to the gainNode
      this.sourceNode.connect(this.gainNode);
      return;
    }

    this.audioContext = new AudioContext();
    this.gainNode = new GainNode(this.audioContext);
    this.analyserNode = new AnalyserNode(this.audioContext);
    this.analyserNode.fftSize = FFT_SIZE;
    this.analyserNode.smoothingTimeConstant = SMOOTHING_CONSTANT;

    // Route the source audio stream (microphone) to the AudioContext
    this.sourceNode = new MediaStreamAudioSourceNode(this.audioContext, {
      mediaStream: stream
    });

    // Send AudioContext to a MediaStream
    this.destinationNode = new MediaStreamAudioDestinationNode(this.audioContext);

    // Connect the sourceNode to the gainNode
    this.sourceNode.connect(this.gainNode);

    // Connect the gain node to handle volume change / mute
    this.gainNode.connect(this.analyserNode);
    this.gainNode.connect(this.destinationNode);

    // Use the MediaStreamAudioDestinationNode for the MediaRecorder stream
    this.audioStream = this.destinationNode.stream;

    this.renderVolume();
    this.renderVisualizer();
    this.renderControls();
  }

  async isMicrophoneAllowed() {
    if (!!navigator?.permissions) {
      await navigator.permissions.query({
        name: 'microphone'
      }).then((permissionStatus) => {
        console.log('[Browser Broadcast] - Permission status: ', permissionStatus);
        return permissionStatus.state === 'granted';
      });
    }
    return 'denied';
  }

  async disconnectMicrophone() {
    if (this.microphoneStream) {
      console.log('[Browser Broadcast] - Disconnecting microphone');
      return new Promise((resolve) => {
        this.microphoneStream.getAudioTracks().forEach(track => {
          track.stop();
          track.enabled = false;
        });
        this.destinationNode.disconnect();
        this.sourceNode.disconnect();
        this.audioContext.close().then(() => {
          resolve();
        });
      });
    }
  }

  iframeLoaded() {
    console.log('[Broadcast] - Channel iFrame loaded');
    if (this.hasLoaderTarget) {
      setTimeout(() => {
        this.loaderTarget.style.display = 'none';
      }, 1000);
    }
  }

  toggleBroadcast() {
    if (!this.audioStream) return;

    if (this.isRecordingValue) {
      const confirmEndBroadcasting = new TurboConfirm();
      const endEventTitle = this.liteNoBroadcastValue ? 'End event?' : 'End stream?';
      const endEventBody = this.liteNoBroadcastValue ? 'End your first event?' : 'You can keep chatting with your listeners.';
      confirmEndBroadcasting.confirmWithContent({
        '#confirm-title': endEventTitle,
        '#confirm-body': endEventBody,
        '#confirm-accept': 'Yep!'
      }).then(response => {
        if (response) {
          this.stopBroadcast();
        }
      });
    } else {
      this.startBroadcast();
    }
  }

  async startBroadcast() {
    console.log('[Browser Broadcast] - startBroadcast');

    if (!this.isMicrophoneAllowed()) {
      this.handleMicrophoneDenied();
      return;
    }

    const broadcastToken = await this.getBroadcastToken();

    this.broadcastButtonTarget.innerHTML = this.broadcastButtonTarget.dataset.submitsWith;
    this.mediaRecorder = new MediaRecorder(this.audioStream, {
      mimeType: 'audio/webm;codecs=opus'
    });
    this.mediaRecorder.start(1000);
    this.dispatch('broadcastHubSend', {
      detail: {
        messageType: 'start_stream',
        data: {
          mime_type: this.mediaRecorder.mimeType,
          event_id: this.eventIdValue,
          broadcast_token: broadcastToken
        }
      }
    });
    this.iframeTarget.contentWindow.postMessage(
      {
        sender: 'browserBroadcasting',
        value: 'start'
      },
      '*'
    );
    this.mediaRecorder.addEventListener('dataavailable', async (e) => {
      if (this.connectionOnline && this.castConnected) {
        console.log('[Broadcast] - Sending data:', e.data);
        this.dispatch('broadcastHubPayloadSend', {
          detail: {
            payload: e.data
          }
        });
      }
    });
    this.mediaRecorder.addEventListener('stop', async (e) => {});
  }

  broadcastStarted(e) {
    // This is called from the cast broadcast:started via
    // this.dispatch('broadcastStarted');
    if (!this.broadcastStartedValue) {
      console.log('[Browser Broadcast] - Broadcast Started from hub event', e);
      this.broadcastUid = e.detail.broadcast_uid;
      this.broadcastStartedValue = true;
      this.isRecordingValue = true;
      this.startTime = Math.floor(Date.now() / 1000);
      this.startTimer();
      this.checkUserBroadcastTime();
      this.audioSettingsVisibleValue = false;
      this.showFirstTimeBroadcastModal();
    }
  }

  async stopBroadcast() {
    console.log('[Browser Broadcast] - stopBroadcast');
    this.dispatch('broadcastHubSend', {
      detail: {
        messageType: 'stop_stream'
      }
    });
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
    }
    this.stopUserBroadcastTime();
    this.stopTimer();

    this.iframeTarget.contentWindow.postMessage(
      {
        sender: 'browserBroadcasting',
        value: 'stop'
      },
      '*'
    );

    if (this.liteNoBroadcastValue) {
      await this.disconnectMicrophone();
      this.visitSummary('lite-first-broadcast');
    } else {
      this.hasFinishedBroadcastValue = true;
      this.isRecordingValue = false;
      this.broadcastStartedValue = false;
    }
  }

  visitSummary(param) {
    let summaryUrl = new URL(this.summaryUrlValue);
    summaryUrl.searchParams.set(param, true);
    Turbo.visit(summaryUrl, { action: 'replace' });
  }

  async closeEvent(event) {
    event.preventDefault();
    await this.disconnectMicrophone();
    Turbo.visit(event.target.href, { action: 'replace' });
  }

  async cancelEvent(event) {
    event.preventDefault();
    const confirmEventCancel = new TurboConfirm();
    await confirmEventCancel.confirmWithContent({
      '#confirm-title': 'Cancel event',
      '#confirm-body': 'Are you sure you want to cancel this event?',
      '#confirm-accept': 'Yep!'
    }).then(response => {
      if (response) {
        this.disconnectMicrophone();
        event.target.closest('form').requestSubmit();
      }
    });
  }

  showFirstTimeBroadcastModal() {
    if (!this.liteNoBroadcastValue) return;

    const broadcastStartedConfirm = new TurboConfirm({
      showConfirmCallback: element => {
        element.showModal && element.showModal();
        document.querySelector('.confirm-cancel').remove();
      }
    });
    broadcastStartedConfirm.confirmWithContent({
      '#confirm-title': "🎉 You're Live!",
      '#confirm-body': `
          <p class="m-0 my-2">Tune in to your broadcast here:</p>
          <a class="flex items-center gap-1 outline-none" href="${this.channelEventUrlValue}" target="_blank">
            <span class="block truncate">${this.channelEventUrlValue}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" class="fill-mixlr-red"><path d="M11,5 C11.5522847,5 12,5.44771525 12,6 C12,6.51283584 11.6139598,6.93550716 11.1166211,6.99327227 L11,7 L5,7 C4.48716416,7 4.06449284,7.38604019 4.00672773,7.88337887 L4,8 L4,19 C4,19.5128358 4.38604019,19.9355072 4.88337887,19.9932723 L5,20 L16,20 C16.5128358,20 16.9355072,19.6139598 16.9932723,19.1166211 L17,19 L17,13 C17,12.4477153 17.4477153,12 18,12 C18.5128358,12 18.9355072,12.3860402 18.9932723,12.8833789 L19,13 L19,19 C19,20.5976809 17.75108,21.9036609 16.1762728,21.9949073 L16,22 L5,22 C3.40231912,22 2.09633912,20.75108 2.00509269,19.1762728 L2,19 L2,8 C2,6.40231912 3.24891996,5.09633912 4.82372721,5.00509269 L5,5 L11,5 Z M21,2 L21.0193545,2.00018615 C21.042681,2.00063489 21.0659949,2.00189498 21.0892501,2.00396641 L21,2 C21.0506203,2 21.1003621,2.00376119 21.1489612,2.01101934 C21.165949,2.01362808 21.1833753,2.01670217 21.2007258,2.02024007 C21.2227461,2.0246398 21.2444365,2.02983363 21.2658396,2.03572097 C21.2814633,2.04009773 21.2968513,2.04472872 21.3121425,2.04973809 C21.3317977,2.05612249 21.3515792,2.06331574 21.3710585,2.07110396 C21.3887956,2.07819642 21.4060985,2.08567905 21.4232215,2.09367336 C21.4438914,2.10337664 21.4642939,2.11379392 21.484277,2.12487577 C21.4963944,2.13149883 21.5086131,2.13860692 21.5207088,2.14599545 C21.546275,2.1617187 21.5711307,2.17849009 21.5951593,2.19631351 C21.602603,2.20175344 21.6098424,2.20728448 21.6170223,2.21292769 C21.6801818,2.26255171 21.7374483,2.31981825 21.787214,2.38324992 L21.7071068,2.29289322 C21.7425008,2.32828727 21.774687,2.36567442 21.8036654,2.40469339 C21.8215099,2.42886931 21.8382813,2.45372505 21.8539326,2.47933981 C21.8613931,2.49138689 21.8685012,2.50360556 21.8753288,2.5159379 C21.8862061,2.53570605 21.8966234,2.5561086 21.9063462,2.57690085 C21.914321,2.5939015 21.9218036,2.61120435 21.9287745,2.62866398 C21.9366843,2.6484208 21.9438775,2.66820232 21.9504533,2.68826359 C21.9552713,2.70314866 21.9599023,2.71853672 21.9641549,2.73400703 C21.9701664,2.75556352 21.9753602,2.77725392 21.9798348,2.7992059 C21.9832978,2.81662474 21.9863719,2.83405099 21.9889822,2.85153313 C21.9905825,2.86176411 21.9920139,2.87254508 21.9932723,2.88337887 L21.9962979,2.9137692 C21.9978436,2.93173451 21.9989053,2.94973363 21.9994829,2.96774538 L22,3 L22,9 C22,9.55228475 21.5522847,10 21,10 C20.4871642,10 20.0644928,9.61395981 20.0067277,9.11662113 L20,9 L20,5.414 L10.7071068,14.7071068 C10.3165825,15.0976311 9.68341751,15.0976311 9.29289322,14.7071068 C8.93240926,14.3466228 8.90467972,13.7793918 9.20970461,13.3871006 L9.29289322,13.2928932 L18.584,4 L15,4 C14.4871642,4 14.0644928,3.61395981 14.0067277,3.11662113 L14,3 C14,2.48716416 14.3860402,2.06449284 14.8833789,2.00672773 L15,2 L21,2 Z"></path></svg>
          </a>
        `,
      '#confirm-accept': 'OK'
    });
  }

  startTimer = () => {
    const timeNow = Math.floor(Date.now() / 1000);
    const diff = timeNow - this.startTime;
    this.checkTime(diff);
    this.broadcastTimerTarget.textContent = formatTime(diff);
    this.broadcastTimerTimeout = setTimeout(this.startTimer, 500);
  }

  stopTimer = (showCurrentTime = false) => {
    console.log('[Browser Broadcast] - stopTimer');
    if (this.broadcastTimerTimeout) {
      clearTimeout(this.broadcastTimerTimeout);
    }
    if (!showCurrentTime) {
      this.broadcastTimerTarget.textContent = formatTime(0);
    }
  }

  checkTime(i) {
    if (i < 10) { i = '0' + i };
    return i;
  }

  async getBroadcastToken() {
    const { response } = await post(this.broadcastTokenUrlValue);
    const { token } = await response.json();
    return token;
  }

  checkUserBroadcastTime() {
    if (!this.broadcastTimeUrlValue) return;

    this.userBroadcastTimeInterval = setInterval(async () => {
      const response = await get(this.broadcastTimeUrlValue, {
        credentials: 'include'
      });
      if (response.ok) {
        const body = await response.json;
        const broadcastTime = body?.data?.attributes?.broadcast_time;
        console.log('[Browser Broadcast] - Checking broadcast time:', broadcastTime);
        if (broadcastTime && broadcastTime.seconds_remaining <= 0) {
          this.stopUserBroadcastTime();
          this.stopTimer(true);
          this.stopBroadcast();
          await this.disconnectMicrophone();
          const outOfTimeConfirm = new TurboConfirm({
            showConfirmCallback: element => {
              element.showModal && element.showModal();
              document.querySelector('.confirm-cancel').remove();
            }
          });
          outOfTimeConfirm.confirmWithContent({
            '#confirm-title': 'Out of time!',
            '#confirm-body': 'You have used up all your broadcast time for today.',
            '#confirm-accept': 'OK'
          }).then(response => {
            if (response) {
              this.visitSummary('out-of-time');
            }
          });
        }

        if (broadcastTime && broadcastTime.seconds_remaining && this.hasTimeRemainingTarget) {
          this.timeRemainingTargets.forEach(target => {
            const minRemaining = Math.floor(broadcastTime.seconds_remaining / 60);
            target.textContent = `${minRemaining}MIN`;
          });
        }
      }
    }, 30000);
  }

  stopUserBroadcastTime() {
    if (this.userBroadcastTimeInterval) {
      clearInterval(this.userBroadcastTimeInterval);
    }
  }

  renderControls() {
    if (this.broadcastButtonTarget) {
      this.broadcastButtonTarget.disabled = false;
    }
    if (this.hasMuteButtonTarget) {
      this.muteButtonTarget.disabled = false;
    }
    if (this.hasAudioSettingsButtonTarget) {
      this.audioSettingsButtonTarget.disabled = false;
    }
  }

  renderVolume() {
    if (!this.volumeTarget) return;
    this.volumeTarget.disabled = false;
    this.updateVolume(0.5);
  }

  changeVolume(event) {
    console.log('[Browser Broadcast] - Changing volume to: ', event.target.value);
    this.updateVolume(event.target.value);
  }

  muteAudio() {
    this.isMutedValue = !this.isMutedValue;
  }

  updateVolume(volume) {
    this.gainNode.gain.value = volume;
  }

  generateMeterLeds = (volume, target) => {
    const leds = [...target.querySelectorAll('div')];
    let range = leds.slice(0, Math.round(volume));

    for (var i = 0; i < leds.length; i++) {
      leds[i].style.backgroundColor = `rgb(255 255 255 / 0.2)`;
    }

    for (var i = 0; i < range.length; i++) {
      range[i].style.backgroundColor = `${ledColor[i]}`;
    }
  };

  renderVisualizer = () => {
    if (!this.audioStream) return;
    const pcmData = new Uint8Array(this.analyserNode.frequencyBinCount);
    const animate = () => {
      this.analyserNode.getByteFrequencyData(pcmData);

      let sum = 0;
      for (let i = 0; i < pcmData.length; i++) {
        sum += pcmData[i];
      }
      const rms = Math.sqrt(sum / pcmData.length);
      
      this.generateMeterLeds(rms, this.leftChannelLedsTarget);
      this.generateMeterLeds(rms, this.rightChannelLedsTarget);
      this.visualizerRequestFrame = requestAnimationFrame(animate);
    };
    this.visualizerRequestFrame = requestAnimationFrame(animate);
  }

  stopVisualizer() {
    if (this.visualizerRequestFrame !== null) {
      window.cancelAnimationFrame(this.visualizerRequestFrame);
      this.visualizerRequestFrame = null;
    }
  }

  handleError = (error) => {
    let confirmTitle = 'Error';
    let confirmBody = 'There was an error setting up the audio devices.';
    let showErrorConfirm = true;
    
    if (error.name == "NotFoundError" || error.name == "DevicesNotFoundError") {
      console.error('[Browser Broadcast] - Error: Required track is missing');
      confirmTitle = 'No devices found';
      confirmBody = "We couldn't find any input devices for your audio.";
    } else if (error.name == "NotReadableError" || error.name == "TrackStartError") {
      console.error('[Browser Broadcast] - Error: Webcam or mic are already in use');
      confirmTitle = 'Microphone already in use';
      confirmBody = 'Your microphone is already in use in another browser tab.';
    } else if (error.name == "OverconstrainedError" || error.name == "ConstraintNotSatisfiedError") {
      console.error('[Browser Broadcast] - Error: Constraints can not be satisfied by avb. devices');
    } else if (error.name == "NotAllowedError" || error.name == "PermissionDeniedError") {
      console.error('[Browser Broadcast] - Error: Permissions denied in the browser');
      showErrorConfirm = false;
      this.handleMicrophoneDenied();
    } else if (error.name == "TypeError" || error.name == "TypeError") {
      console.error('[Browser Broadcast] - Error: Empty constraints object');
    } else {
      console.error('[Browser Broadcast] - Error: ', error);
    }

    if (showErrorConfirm) {
      const errorConfirm = new TurboConfirm();
      errorConfirm.confirmWithContent({
        '#confirm-title': confirmTitle,
        '#confirm-body': confirmBody,
        '#confirm-accept': 'OK',
        '.confirm-cancel': 'Return to dashboard'
      }).then(response => {
        if (response) {
          this.broadcastButtonTarget.setAttribute('disabled', 'disabled');
        } else {
          this.disconnectMicrophone();
          Turbo.visit(this.cancelEventUrlValue, { action: 'replace' });
        }
      });
    }

    this.isRecordingValue = false;
  }

  async handleMicrophoneDenied() {
    const status = await navigator.permissions.query({ name: 'microphone' });
    status.addEventListener('change', async () => {
      await this.getStream().then(this.getDevices).then(this.gotDevices);
    }, { once: true });
    const microphoneDeniedConfirm = new TurboConfirm({
      showConfirmCallback: element => {
        element.showModal && element.showModal();
        document.querySelector('.confirm-cancel').remove();
      }
    });
    microphoneDeniedConfirm.confirmWithContent({
      '#confirm-title': 'Microphone permissions',
      '#confirm-body': `
        <p>To broadcast audio you will need to allow microphone permissions in your browser:</p>
        <ul>
          <li><a href="${this.chromePermissionsUrlValue}" target="_blank">Chrome</a></li>
          <li><a href="${this.firefoxPermissionsUrlValue}" target="_blank">Firefox</a></li>
        </ul>
        <p>You can also broadcast using our apps on Desktop, iOS & Android</p>
        <a href="${this.appDownloadUrlValue}" target="_blank">Download apps</a>
      `,
      '#confirm-accept': 'OK'
    }).then(response => {
      if (response) {
        this.broadcastButtonTarget.disabled = true;
      }
    });
  }

  toggleAudioSettings() {
    this.audioSettingsVisibleValue = !this.audioSettingsVisibleValue;
  }

  hideAudioSettings() {
    this.audioSettingsVisibleValue = false;
  }

  broadcastHubDisconnected() {
    console.log('broadcastHubDisconnected');
    this.castConnected = false;
    this.connectionStatus(this.connectionOnline);

    if (this.mediaRecorder) this.mediaRecorder.stop();
  }

  async broadcastHubConnected() {
    console.log('broadcastHubConnected');
    this.castConnected = true;
    this.connectionStatus(this.connectionOnline);

    if (this.isRecordingValue && this.broadcastUid) {
      const broadcastToken = await this.getBroadcastToken();

      this.mediaRecorder.start(1000);
      this.dispatch('broadcastHubSend', {
        detail: {
          messageType: 'restart_stream',
          data: {
            mime_type: this.mediaRecorder.mimeType,
            broadcast_uid: this.broadcastUid,
            broadcast_token: broadcastToken
          }
        }
      });
    }
  }

  connectionStatus(onLine) {
    if (onLine && this.castConnected) {
      this.networkBannerTarget.style.display = 'none';
      this.networkBannerTarget.setAttribute('aria-hidden', true);
      this.statusBarTarget.classList.remove('!bg-mixlr-orange');
      this.liveStatusTarget.textContent = this.isRecordingValue ? 'Live now' : 'Off Air';
      this.dispatch('broadcastHubNetworkStatus', {
        detail: {
          status: {
            onLine: true
          }
        }
      });
    } else {
      this.networkBannerTarget.style.display = 'flex';
      this.networkBannerTarget.setAttribute('aria-hidden', false);
      this.statusBarTarget.classList.add('!bg-mixlr-orange');
      this.liveStatusTarget.textContent = 'Offline';
      this.dispatch('broadcastHubNetworkStatus', {
        detail: {
          status: {
            onLine: onLine
          }
        }
      });
    }
    this.connectionOnline = onLine;
  }

  audioSettingsVisibleValueChanged() {
    if (this.audioSettingsVisibleValue) {
      this.audioSettingsTarget.style.display = 'block';
    } else {
      this.audioSettingsTarget.style.display = 'none';
    }
  }

  isMutedValueChanged() {
    if (!this.audioStream) return;
    if (this.isMutedValue) {
      this.muteButtonTarget.classList.remove('bg-white/20');
      this.muteButtonTarget.classList.add('bg-mixlr-red');
      this.gainNode.gain.value = 0;
    } else {
      this.muteButtonTarget.classList.remove('bg-mixlr-red');
      this.muteButtonTarget.classList.add('bg-white/20');
      this.gainNode.gain.value = this.volumeTarget.value;
    }
  }

  isRecordingValueChanged() {
    if (!this.audioStream) return;
    if (this.isRecordingValue) {
      this.liveStatusTarget.textContent = "Live now";
      this.broadcastButtonTarget.innerHTML = 'Stop';
      //this.audioSelectTarget.setAttribute('disabled', 'disabled');
      this.statusBarTarget.classList.add('bg-mixlr-green');
      this.statusBarTarget.classList.remove('bg-white/[15%]');
      this.listenLiveButtonTarget.style.display = 'inline-flex';
      this.cancelEventButtonTarget.style.display = 'none';
      this.closeEventButtonTarget.style.display = 'none';
      this.broadcastTimerTarget.style.display = 'block';
    } else {
      this.liveStatusTarget.textContent = "Off Air";
      this.broadcastButtonTarget.innerHTML = this.hasFinishedBroadcastValue ? 'Resume' : 'Start';
      //this.audioSelectTarget.removeAttribute('disabled');
      this.statusBarTarget.classList.remove('bg-mixlr-green');
      this.statusBarTarget.classList.add('bg-white/[15%]');
      this.listenLiveButtonTarget.style.display = 'none';
      this.cancelEventButtonTarget.style.display = this.hasFinishedBroadcastValue ? 'none' : 'flex';
      this.closeEventButtonTarget.style.display = 'flex';
      this.broadcastTimerTarget.style.display = 'none';
    }
  }

  checkNetworkConnection() {
    const checkOnlineStatus = async () => {
      const onLine = await this.isOnline();
      this.connectionStatus(onLine);
    };
    this.connectionStatusInterval = setInterval(checkOnlineStatus, 10000);
    checkOnlineStatus();
  }

  async isOnline() {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      img.onerror = () => {
        resolve(false);
      };
      img.onload = () => {
        resolve(true);
      };
      img.src = 'https://www.google.com/favicon.ico?_=' + ((new Date()).getTime());
    });
  }
}